import { Flame, ExternalLink, Navigation, Share2 } from 'lucide-react';
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { WildfireShareSheet } from '@/components/wildfires/WildfireShareSheet';
import { openDirections } from '@/utils/share';
import { wildfireMarkerPosition, type WildfireMapShape } from '@/utils/wildfireMapStyle';
import { wildfireStateLabel } from '@/utils/wildfireShare';

export interface WildfireDrawerRecord extends WildfireMapShape {
  id: number | string;
  fire_reference?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  suburb?: string | null;
  address?: string | null;
  area_size_hectares?: number | null;
  type?: string | null;
}

interface WildfireDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wildfire: WildfireDrawerRecord | null;
  /** Omitted on the public map, which has no detail page to send anyone to. */
  onViewDetails?: (id: number | string) => void;
  /**
   * The FPA whose fires this map is showing — forwarded to the share sheet's
   * mutual-aid picker so that FPA is left out of its own options. The map row
   * (`WildfireListItem`) carries no tenant_id, so the page has to supply it.
   */
  tenantId?: number | null;
  formatDateTime?: (value?: string | null) => string;
}

// Label comes from the share builder so the badge and the shared text can
// never disagree about whether a fire is out.
function statusOf(w: WildfireDrawerRecord): { label: string; className: string } {
  const label = wildfireStateLabel(w);
  if (label === 'Draft') {
    return { label, className: 'bg-gray-100 text-gray-700 border-gray-300' };
  }
  return label === 'Active'
    ? { label, className: 'bg-red-100 text-red-800 border-red-300' }
    : { label, className: 'bg-green-100 text-green-800 border-green-300' };
}

/**
 * Bottom drawer for a wildfire marker, replacing the Leaflet popup.
 *
 * A popup is a small box anchored to the pin: on a phone it covers the map,
 * clips its own content, and puts the actions somewhere your thumb isn't.
 * This matches the water-source drawer, so every marker on every map now
 * opens the same way.
 */
export function WildfireDetailDrawer({
  open,
  onOpenChange,
  wildfire,
  onViewDetails,
  tenantId,
  formatDateTime,
}: WildfireDetailDrawerProps) {
  const [shareOpen, setShareOpen] = useState(false);
  if (!wildfire) return null;

  const w = wildfire;
  const status = statusOf(w);
  const title = w.fire_reference || `Wildfire #${w.id}`;
  const marker = wildfireMarkerPosition(w);
  const fmt = formatDateTime ?? ((v?: string | null) => (v ? new Date(v).toLocaleString() : 'N/A'));

  const rows: Array<[string, string | null | undefined]> = [
    ['Started', w.start_time ? fmt(w.start_time) : null],
    ['Suburb', w.suburb],
    ['Address', w.address],
    ['Area', w.area_size_hectares != null ? `${w.area_size_hectares.toLocaleString()} ha` : null],
    ['Type', w.type],
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl px-4 pb-8">
          <SheetHeader className="mb-3">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Flame className="h-4 w-4 text-primary" />
              <span className="truncate">{title}</span>
            </SheetTitle>
            <SheetDescription className="sr-only">Wildfire details</SheetDescription>
            <div className="mt-1">
              <Badge variant="outline" className={`text-xs font-normal ${status.className}`}>
                {status.label}
              </Badge>
            </div>
          </SheetHeader>

          <div className="space-y-1.5 text-sm">
            {rows.map(([label, value]) =>
              value ? (
                <div key={label} className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
                  <span className="break-words">{value}</span>
                </div>
              ) : null,
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {onViewDetails && (
              <Button size="sm" onClick={() => onViewDetails(w.id)} className="gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" />
                View details
              </Button>
            )}
            {marker && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openDirections(marker[0], marker[1], title)}
                className="gap-1.5"
              >
                <Navigation className="h-3.5 w-3.5" />
                Get directions
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} className="gap-1.5">
              <Share2 className="h-3.5 w-3.5" />
              Share
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Shared with the report page so the two never drift. Note the
          shared text is narrower than the rows above: it carries no burn
          area, by design. */}
      <WildfireShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        wildfire={w}
        tenantId={tenantId}
        formatDateTime={fmt}
      />
    </>
  );
}

export default WildfireDetailDrawer;
